/**
 * alle Kommunikationen an zentraler Stelle (neue Kunden)
 */
import type { FirmaExportPortal } from '@ahd/shared/types/ahd-cars/firma/export-portale';
import type { Firma } from '@ahd/shared/types/ahd-cars/firma/firma';
import type { FullFirma } from '@ahd/shared/types/ahd-cars/firma/full';
import type { FirmaMitarbeiter } from '@ahd/shared/types/ahd-cars/firma/mitarbeiter';
import type { FirmaStandort } from '@ahd/shared/types/ahd-cars/firma/standort';
import type { AxiosRequestConfig } from 'axios';
import { type Event } from '../einstellungen/events/Main';
import axios from './Axios';
import { getAxiosHeader, getAxiosHeaderForce, urlImagesRelease, urlSRelease, urlServicesRelease } from './AxiosVars';
import { getIdUser } from './Session';
import type { UploadConfig } from './inputfields/Image';

export function getFirma(idFirma: number, signal: AbortController['signal']) {
    const url = urlServicesRelease + '/firma3/v1/firma/' + idFirma;
    return axios<FullFirma>({
        title: 'Abrufen der Firmendaten',
        method: 'get',
        url: url,
        headers: getAxiosHeaderForce(),
        signal: signal,
    });
}

export function switchFirma(idUser: number) {
    const url = urlSRelease + '/session/switch/' + idUser;
    return axios({
        title: 'Firmenwechsel',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getFirmaMitarbeiter(idFirma: number) {
    const url = urlServicesRelease + '/firma3/v1/firma/' + idFirma + '/mitarbeiter';
    return axios<FirmaMitarbeiter[]>({
        title: 'Abrufen der FirmaMitarbeiter',
        method: 'get',
        url: url,
        headers: getAxiosHeaderForce(),
    });
}

export function saveFirma(data, isCreate: boolean) {
    const url = urlServicesRelease + '/firma3/v1/firma';
    return axios({
        title: 'Speichern der Firmendaten',
        method: isCreate ? 'put' : 'post',
        url: url,
        headers: getAxiosHeader(),
        data: data,
    });
}

export function saveFirmaTracking(idFirma: number, data) {
    const url = urlServicesRelease + '/firma3/v1/firma/' + idFirma + '/trackingSettings';
    return axios({
        title: 'Speichern der Firmen-Trackingdaten',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
        data: data,
    });
}

export function getMitarbeiter(idMitarbeiter: number) {
    const url = urlServicesRelease + '/firma3/v1/mitarbeiter/' + idMitarbeiter;
    return axios<FirmaMitarbeiter>({
        title: 'Abrufen des Mitarbeiters',
        method: 'get',
        url: url,
        headers: getAxiosHeaderForce(),
    });
}

export function saveMitarbeiter(data, isCreate: boolean) {
    const url = urlServicesRelease + '/firma3/v1/mitarbeiter';
    return axios({
        title: 'Speichern des Mitarbeiters',
        method: isCreate ? 'put' : 'post',
        url: url,
        headers: getAxiosHeader(),
        data: data,
    });
}

export function uploadProfilePicture(user: [number, number], data, config: UploadConfig) {
    const url = urlImagesRelease + '/s/v1/firma/' + user[0] + '/' + user[1] + '/userbild/user.jpg';
    return axios({
        title: 'Hochladen des Profilbildes',
        method: 'put',
        url: url,
        headers: getAxiosHeader(),
        data: data,
        ...config,
    });
}

export function removeProfilePicture(user: [number, number]) {
    const url = urlImagesRelease + '/s/v1/firma/' + user[0] + '/' + user[1] + '/userbild/user.jpg';
    return axios({
        title: 'Entfernen des Profilbildes',
        method: 'delete',
        url: url,
        headers: getAxiosHeader(),
    });
}

// ---------------------Verträge ----------------------------------------------------------------------------------------

export function getVertrage() {
    const url = urlServicesRelease + '/firma3/v1/vertrag';
    return axios({
        title: 'Abrufen aller Verträge',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function aktVertrag(idVertrag: number, datum: number) {
    const url = urlServicesRelease + '/firma3/v1/vertrag/a/' + idVertrag + '/' + datum;
    return axios({
        title: 'Aktivieren des Vertrags',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function buyArtikel(idFirma: number, idArtikel: number, datum: number) {
    const url = urlServicesRelease + '/firma3/v1/vertrag/c/' + idFirma + '/' + idArtikel + '/' + datum;
    return axios({
        title: 'Anlegen des Vertrags',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getVertrageFirma(idFirma: number, config?: AxiosRequestConfig) {
    const url = urlServicesRelease + '/firma3/v1/vertrag/firma/' + idFirma;
    return axios({
        title: 'Abrufen der Verträge',
        method: 'get',
        url: url,
        headers: getAxiosHeader(config?.customToken, config?.customTokenHeaderName),
    });
}

export function delVertrag(idVertrag: number) {
    const url = urlServicesRelease + '/firma3/v1/vertrag/f/' + idVertrag;
    return axios({
        title: 'Löschen des Vertrags',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function aktVertragKostenlos(idVertrag: number) {
    const url = urlServicesRelease + '/firma3/v1/vertrag/t/' + idVertrag;
    return axios({
        title: 'Kostenlose Vergabe des Vertrags',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function setTestphaselonger(idVertrag: number, testphaseEndDatum: number) {
    const url = urlServicesRelease + '/firma3/v1/vertrag/t/' + idVertrag + '/' + testphaseEndDatum;
    return axios({
        title: 'Verlängern der Testphase',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getFirmen() {
    const url = urlServicesRelease + '/firma3/v1/firma/valid_list_small';
    return axios<
        Pick<
            Firma,
            'do' | 'f' | 'g' | 'hm' | 'i' | 'lat' | 'latitude' | 'lng' | 'longitude' | 'm' | 'n' | 'o' | 'openMoFr' | 'openSa' | 'p' | 'rg' | 'rn' | 's' | 'sh' | 't' | 'typ' | 'uid' | 'usrNames'
        >[]
    >({
        title: 'Abrufen der Firmen',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

/*
 * alle Standorte, für die Firma
 */
export function getStandorteFirma(idFirma: number) {
    const url = urlServicesRelease + '/firma3/v1/standort/' + idFirma;
    return axios<FirmaStandort[]>({
        title: 'Abrufen der Firmenstandorte',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export type PortalSave = Pick<FirmaExportPortal, 'email' | 'id_portal' | 'kdid' | 'kdnr' | 'login' | 'pwd'> & { cond?: string; export: 0 | 1 };
export function savePortal(idFirma: number, idPortal: number, data: PortalSave) {
    return axios({
        title: 'Ändern des Portals',
        method: 'post',
        url: `${urlServicesRelease}/firma3/v2/portal/${idFirma}/${idPortal}`,
        data,
        headers: getAxiosHeader(),
    });
}

export function getEvents() {
    const url = urlServicesRelease + '/firma3/v1/firma/event/firma';
    return axios({
        title: 'Abrufen der Events',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function saveEvents(id_ereignis: number, formData: Event) {
    const url = urlServicesRelease + '/firma3/v1/firma/event/save';
    const params = new URLSearchParams();
    params.append('id_ereignis', id_ereignis.toString());
    params.append('id_user_to', formData?.id_user_to.toString());
    params.append('sendMail', formData?.sendMail.toString());
    params.append('vorlage', formData?.vorlage);
    return axios({
        title: 'Speichern des Events',
        method: 'post',
        url: url,
        data: params,
        headers: getAxiosHeader(),
    });
}

export function getTexte() {
    const url = urlServicesRelease + '/firma3/v1/firma/texte';
    return axios({
        title: 'Abrufen der AGB-Texte',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function saveTexte(data) {
    const url = urlServicesRelease + '/firma3/v1/firma/texte';
    return axios({
        title: 'Speichern der AGB-Texte',
        method: 'post',
        url: url,
        data: data,
        headers: getAxiosHeader(),
    });
}

export function saveOeffungszeiten(idFirma: number, data: any) {
    const url = urlSRelease + '/firma/save_settings_oeffnungszeiten/' + idFirma + '/' + getIdUser();
    return axios({
        title: 'Speichern der allgemeinen Öffnungszeiten',
        method: 'post',
        url: url,
        data: data,
        headers: getAxiosHeader(),
    });
}

export function saveOeffungszeitenService(idFirma: number, data: any) {
    const url = urlSRelease + '/firma/save_settings_oeffnungszeitenService/' + idFirma + '/' + getIdUser();
    return axios({
        title: 'Speichern der Service-Öffnungszeiten',
        method: 'post',
        url: url,
        data: data,
        headers: getAxiosHeader(),
    });
}

export function saveOeffungszeitenWerkstatt(idFirma: number, data: any) {
    const url = urlSRelease + '/firma/save_settings_oeffnungszeitenWerkstatt/' + idFirma + '/' + getIdUser();
    return axios({
        title: 'Speichern der Werkstatt-Öffnungszeiten',
        method: 'post',
        url: url,
        data: data,
        headers: getAxiosHeader(),
    });
}
